import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Animated
} from "react-native";

import LanguagePicker from "../../../components/LanguagePicker";
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { useLanguageStore } from "../../../features/quiz-core/languageStore";
import { t } from "../../../i18n/translations";
import { createAuthStyles } from "../AuthScreen.styles";

interface AuthFormProps {
  mode: "login" | "signup";
  setMode: (mode: "login" | "signup") => void;
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (pw: string) => void;
  name: string;
  setName: (name: string) => void;
  language: string;
  setLanguage: (lang: string) => void;
  setGlobalLanguage: (lang: string) => void;
  error: string;
  loading: boolean;
  emailFocused: boolean;
  setEmailFocused: (v: boolean) => void;
  passwordFocused: boolean;
  setPasswordFocused: (v: boolean) => void;
  handleAuth: () => void;
  buttonScale: number;
  buttonShadow: number;
  shimmerTranslate: any;
  reduceMotion: boolean;
  setButtonPressed: (v: boolean) => void;
}

const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  setMode,
  email,
  setEmail,
  password,
  setPassword,
  name,
  setName,
  language,
  setLanguage,
  setGlobalLanguage,
  error,
  loading,
  emailFocused,
  setEmailFocused,
  passwordFocused,
  setPasswordFocused,
  handleAuth,
  buttonScale,
  buttonShadow,
  shimmerTranslate,
  reduceMotion,
  setButtonPressed
}) => {
  const { colors } = useAppTheme();
  const styles = createAuthStyles(colors);
  const [showPassword, setShowPassword] = React.useState(false);
  const { language: currentLanguage } = useLanguageStore();

  return (
    <>
    {/* Email Input */}
    <View style={styles.inputContainer}>
      <Text style={styles.inputLabel}>{t('auth.email', currentLanguage)}</Text>
      <TextInput
        style={[
          styles.input,
          emailFocused ? styles.inputFocused : null,
          error ? styles.inputError : null
        ]}
        placeholder={t('auth.email', currentLanguage)}
        placeholderTextColor="#777587"
        autoCapitalize="none"
        value={email}
        onChangeText={setEmail}
        keyboardType="email-address"
        onFocus={() => setEmailFocused(true)}
        onBlur={() => setEmailFocused(false)}
        autoComplete="email"
      />
    </View>
      {/* Password Input */}
      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>{t('auth.password', currentLanguage)}</Text>
        <View style={{ position: 'relative', width: '100%' }}>
          <TextInput
            style={[
              styles.input,
              passwordFocused ? styles.inputFocused : null,
              error ? styles.inputError : null,
              { paddingRight: 44 }
            ]}
            placeholder={t('auth.password', currentLanguage)}
            placeholderTextColor="#777587"
            secureTextEntry={!showPassword}
            value={password}
            onChangeText={setPassword}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            autoComplete="password"
          />
          <TouchableOpacity
            style={{ position: 'absolute', right: 12, top: 0, height: '100%', justifyContent: 'center', alignItems: 'center', width: 32 }}
            onPress={() => setShowPassword((v) => !v)}
            accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
          >
            <Text style={{ color: '#464555', fontSize: 22, opacity: 0.7 }}>
              {showPassword ? '🙈' : '👁️'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      {/* Name Input for Signup */}
      {mode === "signup" && (
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>{t('auth.name', currentLanguage)}</Text>
          <TextInput
            style={styles.input as any}
            placeholder={t('auth.howShouldWeCallYou', currentLanguage)}
            placeholderTextColor="#777587"
            value={name}
            onChangeText={setName}
            autoComplete="name"
          />
        </View>
      )}
      {/* Language Picker for Signup */}
      {mode === "signup" && (
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>{t('auth.language', currentLanguage)}</Text>
          <View style={styles.languagePickerContainer}>
            <LanguagePicker
              value={language}
              onChange={(lang) => {
                setLanguage(lang);
                setGlobalLanguage(lang);
              }}
            />
          </View>
        </View>
      )}
      {/* Error Message */}
      {error ? (
        <Text style={{ color: '#EF4444', marginTop: 8, marginBottom: 0, textAlign: 'center' }}>{error}</Text>
      ) : null}
      {/* Mode Switch Link */}
      <View style={{ marginTop: 24, alignItems: 'center' }}>
        {mode === 'login' ? (
          <TouchableOpacity onPress={() => setMode('signup')}>
            <Text style={{ color: '#4F46E5', textDecorationLine: 'underline', fontWeight: 'bold' }}>
              {t('auth.newHere', currentLanguage)}
            </Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity onPress={() => setMode('login')}>
            <Text style={{ color: '#4F46E5', textDecorationLine: 'underline', fontWeight: 'bold' }}>
              {t('auth.alreadyHaveAccount', currentLanguage)}
            </Text>
          </TouchableOpacity>
        )}
      </View>
      {/* Submit Button */}
      <LinearGradient
        colors={["#4F46E5", "#4338CA"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[
          styles.button,
          { transform: [{ scale: buttonScale }] }
        ]}
      >
        <TouchableOpacity
          style={{ width: "100%", alignItems: "center", justifyContent: "center" }}
          onPress={handleAuth}
          onPressIn={() => setButtonPressed(true)}
          onPressOut={() => setButtonPressed(false)}
          disabled={loading}
          accessibilityLabel={mode === "login" ? t('auth.signIn', currentLanguage) : t('auth.signUp', currentLanguage)}
        >
          <Text style={styles.buttonLabel}>
            {mode === "login" ? t('auth.signIn', currentLanguage) : t('auth.signUp', currentLanguage)}
          </Text>
          {loading && (
            <ActivityIndicator style={{ marginLeft: 8 }} color="#fff" />
          )}
        </TouchableOpacity>
        {/* Shimmer effect overlay */}
        {!reduceMotion && (
          <Animated.View
            pointerEvents="none"
            style={[
              styles.shimmer,
              { transform: [{ translateX: shimmerTranslate }] }
            ]}
          >
            <LinearGradient
              colors={["#fff", "#C3C0FF", "#fff"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.shimmerGradient}
            />
          </Animated.View>
        )}
      </LinearGradient>
    </>
  );
};

export default AuthForm;
